import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { diffMetrics } from './metricsDiff';

const fmtMoney = (v, opts = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sign = opts.signed && v > 0 ? '+' : '';
  return `${sign}$${Math.round(v).toLocaleString()}`;
};
const fmtPct = (v, opts = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sign = opts.signed && v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
};
const fmtRatio = (v, opts = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sign = opts.signed && v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}`;
};

const ROWS = [
  { key: 'monthly_cash_flow',  i18n: 'financialImpact.monthlyCashFlow',  fmt: fmtMoney, deltaSuffix: '/mo' },
  { key: 'annual_cash_flow',   i18n: 'financialImpact.annualCashFlow',   fmt: fmtMoney, deltaSuffix: '/yr' },
  { key: 'noi',                i18n: 'financialImpact.noi',              fmt: fmtMoney, deltaSuffix: '/yr' },
  { key: 'cap_rate',           i18n: 'financialImpact.capRate',          fmt: fmtPct },
  { key: 'dscr',               i18n: 'financialImpact.dscr',             fmt: fmtRatio },
  { key: 'roi',                i18n: 'financialImpact.roi',              fmt: fmtPct },
  { key: 'cash_on_cash',       i18n: 'financialImpact.cashOnCash',       fmt: fmtPct },
  { key: 'monthly_mortgage',   i18n: 'financialImpact.monthlyMortgage',  fmt: fmtMoney, deltaSuffix: '/mo' },
];

const DeltaBadge = ({ row, entry }) => {
  const { delta } = entry;
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return <span data-testid={`fi-delta-${row.key}`} className="text-slate-400">—</span>;
  }
  const zero = Math.abs(delta) < 0.005;
  const positive = delta > 0 && !zero;
  const cls = zero
    ? 'text-slate-500 dark:text-slate-400'
    : positive
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-red-600 dark:text-red-400';
  const Icon = zero ? Minus : (positive ? TrendingUp : TrendingDown);
  return (
    <span data-testid={`fi-delta-${row.key}`} className={`inline-flex items-center gap-1 font-primary font-bold tabular-nums ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {row.fmt(delta, { signed: true })}
      {row.deltaSuffix && !zero && <span className="text-[10px] font-normal opacity-70">{row.deltaSuffix}</span>}
    </span>
  );
};

const FinancialImpact = ({ original, current, loading }) => {
  const { t } = useTranslation();
  const diff = diffMetrics(current, original);

  return (
    <section
      data-testid="financial-impact-section"
      className="mt-8 bg-white dark:bg-[#131824] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100">
          {t('financialImpact.title')}
        </h2>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        {t('financialImpact.subtitle')}
      </p>

      {/* Desktop: grid header. Mobile: card stack per row. */}
      <div className="hidden md:grid grid-cols-[minmax(0,1.2fr)_1fr_1fr_1fr] gap-2 pb-2 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
        <span>{t('financialImpact.metric')}</span>
        <span>{t('scenario.originalDeal')}</span>
        <span>{t('scenario.currentScenario')}</span>
        <span>{t('financialImpact.delta')}</span>
      </div>

      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {ROWS.map((row) => {
          const entry = diff[row.key] || { original: null, current: null, delta: null };
          return (
            <div key={row.key}
                 data-testid={`fi-row-${row.key}`}
                 className="grid grid-cols-2 md:grid-cols-[minmax(0,1.2fr)_1fr_1fr_1fr] gap-2 py-3 items-center">
              <span className="col-span-2 md:col-span-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                {t(row.i18n)}
              </span>
              <span data-testid={`fi-original-${row.key}`}
                    className="text-sm font-mono tabular-nums text-slate-600 dark:text-slate-400">
                <span className="md:hidden text-[10px] uppercase tracking-wider block text-slate-400">
                  {t('scenario.originalDeal')}
                </span>
                {row.fmt(entry.original)}
              </span>
              <span data-testid={`fi-current-${row.key}`}
                    className="text-sm font-mono tabular-nums text-slate-900 dark:text-slate-100 font-semibold">
                <span className="md:hidden text-[10px] uppercase tracking-wider block text-slate-400">
                  {t('scenario.currentScenario')}
                </span>
                {row.fmt(entry.current)}
              </span>
              <span className="text-sm">
                <span className="md:hidden text-[10px] uppercase tracking-wider block text-slate-400">
                  {t('financialImpact.delta')}
                </span>
                <DeltaBadge row={row} entry={entry} />
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FinancialImpact;
